import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { MapPin, ArrowRight, Phone, Calendar } from 'lucide-react';
import { areasServed } from '@/data/areasServed';

const AreasWeServePage = () => {
  return (
    <div className="min-h-screen" data-testid="areas-we-serve-page">
      {/* Breadcrumb */}
      <div className="bg-muted py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-2 text-sm">
            <Link to="/" className="text-muted-foreground hover:text-primary">Home</Link>
            <span className="text-muted-foreground">/</span>
            <span className="text-foreground font-medium">Areas We Serve</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="bg-gradient-hero py-16 lg:py-24 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-6">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-semibold">Serving All of Brevard County</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Areas We Serve
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed">
              Melbourne Hand Center provides specialized hand, wrist, and elbow care
              to patients throughout Brevard County, Florida. Our board-certified,
              fellowship-trained specialists deliver advanced surgical and non-surgical
              treatments tailored to each patient.
            </p>
          </div>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-10">
            <h2 className="font-display text-2xl sm:text-3xl font-bold text-foreground mb-3">
              Communities We Serve in Brevard County, FL
            </h2>
            <p className="text-muted-foreground">
              Click any city below for location-specific services and information.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {areasServed.map((area) => (
              <Link
                key={area.slug}
                to={`/areas-we-serve/${area.slug}`}
                data-testid={`area-card-${area.slug}`}
                className="group"
              >
                <Card className="bg-card border-0 shadow-card hover:shadow-lg hover:-translate-y-1 transition-all duration-300 h-full">
                  <CardContent className="p-6 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <MapPin className="w-5 h-5 text-primary group-hover:text-primary-foreground" />
                      </div>
                      <div>
                        <h3 className="font-display text-lg font-semibold text-foreground group-hover:text-primary transition-colors">
                          {area.name}
                        </h3>
                        <p className="text-xs text-muted-foreground">Brevard County, FL</p>
                      </div>
                    </div>
                    <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all flex-shrink-0" />
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 lg:py-20 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute -top-20 -right-20 w-96 h-96 bg-primary-foreground/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-primary-foreground mb-4">
            Don't See Your City?
          </h2>
          <p className="text-lg text-primary-foreground/90 mb-8 max-w-2xl mx-auto">
            We welcome patients from across Brevard County and the Space Coast region.
            Call us today to schedule your consultation.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button variant="white" size="xl" asChild>
              <a
                href="https://app.acuityscheduling.com/schedule.php?owner=13861726"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="areas-cta-schedule"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Schedule Consultation
              </a>
            </Button>
            <Button variant="heroOutline" size="xl" asChild>
              <a href="tel:321-500-4263" data-testid="areas-cta-call">
                <Phone className="w-5 h-5 mr-2" />
                Call 321.500.4263
              </a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AreasWeServePage;
